import { textBlocks } from './htmlText.ts';
import { parseJavaProperties } from './javaProperties.ts';
import { ENTITY_TYPE_NAMES } from './model.ts';
import { int, text, type SqlConnection } from './sql.ts';

/**
 * Finding text in a realm's entries (docs/SEARCH-PLAN.md), matching as the desktop's
 * Find does (`search.SearchQuery`, `search.PlainText`): plain text, Match case, Whole
 * words. schema/fixtures/search-cases.txt keeps the two in step. Regular expressions
 * aren't offered (section 3.4).
 */

export interface SearchQuery {
  readonly text: string;
  readonly matchCase: boolean;
  /** No letter, digit or `_` right before or after, so "Tom" doesn't find "Tomas". */
  readonly wholeWords: boolean;
}

export interface TextMatch {
  /** `[start, end)` in UTF-16 units, as Java's `Matcher` counts. */
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

// The pattern's syntax characters; with the `u` flag, escaping anything else (`-`, say) is an error.
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

/** `SearchQuery.pattern()`, for plain text: `Pattern.quote`, with the whole-word guards and Unicode case. */
export function searchPattern(query: SearchQuery): RegExp {
  if (query.text === '') throw new Error('Nothing to find');
  let body = escapeRegExp(query.text);
  if (query.wholeWords) body = `(?<![\\p{L}\\p{N}_])(?:${body})(?![\\p{L}\\p{N}_])`;
  return new RegExp(body, query.matchCase ? 'gu' : 'giu');
}

/** `PlainText.find`: every match in `text`, empty ones skipped. */
export function findInText(value: string, pattern: RegExp): TextMatch[] {
  const matches: TextMatch[] = [];
  if (value === '') return matches;
  pattern.lastIndex = 0;
  for (let m = pattern.exec(value); m !== null; m = pattern.exec(value)) {
    if (m[0] === '') {
      pattern.lastIndex += 1;
      continue;
    }
    matches.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
  }
  return matches;
}

const CONTEXT = 60;
/** Java's `\s`, which `PlainText` collapses: ASCII whitespace only. */
const JAVA_SPACE_RUN = /[ \t\n\x0B\f\r]+/g;
/** `Character.isWhitespace`, which `String.stripLeading` and `stripTrailing` strip: not the no-break spaces. */
const JAVA_WHITESPACE = '[\\t\\n\\u000B\\f\\r\\u001C-\\u001F \\u1680\\u2000-\\u2006\\u2008-\\u200A\\u2028\\u2029\\u205F\\u3000]';
const LEADING = new RegExp(`^${JAVA_WHITESPACE}+`);
const TRAILING = new RegExp(`${JAVA_WHITESPACE}+$`);
const trimStartJava = (s: string) => s.replace(LEADING, '');
const trimEndJava = (s: string) => s.replace(TRAILING, '');

/** `PlainText.before`: up to 60 characters before `index`, on one line. */
export function snippetBefore(value: string, index: number): string {
  const from = Math.max(0, index - CONTEXT);
  return (from > 0 ? '…' : '') + trimStartJava(value.slice(from, index).replace(JAVA_SPACE_RUN, ' '));
}

/** `PlainText.after`: up to 60 characters after `index`, on one line. */
export function snippetAfter(value: string, index: number): string {
  const to = Math.min(value.length, index + CONTEXT);
  return trimEndJava(value.slice(index, to).replace(JAVA_SPACE_RUN, ' ')) + (to < value.length ? '…' : '');
}

// --- Across entries ----------------------------------------------------------------------

/** Up to this many entries' text is read one by one in a search; beyond it, all at once. */
const ONE_BY_ONE = 50;

export type SearchField = 'title' | 'aliases' | 'labels' | 'field-name' | 'field' | 'text';

/** Where Replace works, as the desktop's Find and Replace: not labels or field names. */
export const REPLACEABLE: ReadonlySet<SearchField> = new Set(['title', 'aliases', 'field', 'text']);

export interface SearchHit {
  readonly field: SearchField;
  /** Where, as shown: "Title", "Field: Born", "Text". */
  readonly where: string;
  readonly before: string;
  readonly text: string;
  readonly after: string;
}

export interface EntryHits {
  readonly entryId: string;
  readonly title: string;
  readonly typeName: string;
  /** Every match in the entry. */
  readonly count: number;
  /** Those a replace would change: not labels or field names, which Replace leaves alone (docs/REPLACE-PLAN.md 3.1). */
  readonly replaceable: number;
  /** The entry's text version when searched, so a replace can tell if it changed since. */
  readonly version: string;
  /** The first ones, up to `hitsPerEntry`. */
  readonly hits: readonly SearchHit[];
}

export interface SearchResults {
  readonly entries: readonly EntryHits[];
  readonly matches: number;
  /** Entries with a match, which can be more than `entries` lists. */
  readonly entriesMatched: number;
  /** Locked entries, whose text wasn't searched. */
  readonly lockedSkipped: number;
}

export interface SearchOptions {
  readonly maxEntries?: number;
  readonly hitsPerEntry?: number;
  /**
   * An entry's text blocks. `version` changes whenever its text does (the entry's
   * modified time and the text's length); `load` reads the HTML, so a cache that already
   * has this version need never fetch it. The worker passes one that caches.
   */
  readonly blocksOf?: (entryId: string, version: string, load: () => string) => readonly string[];
}

/**
 * Searches every entry's title, aliases, labels, field names and values, and text
 * (section 3.1); a locked entry's text is never read, as on the desktop, and counted
 * instead. Entries come in title order.
 */
export function searchEntries(conn: SqlConnection, query: SearchQuery, options: SearchOptions = {}): SearchResults {
  const pattern = searchPattern(query);
  const maxEntries = options.maxEntries ?? 200;
  const hitsPerEntry = options.hitsPerEntry ?? 5;
  const blocksOf = options.blocksOf ?? ((_id: string, _version: string, load: () => string) => textBlocks(load()));
  // The text itself isn't read here: only its version, so cached entries cost nothing to fetch.
  const rows = conn.all(
    `SELECT e.id, e.title, e.entity_type, e.custom_type_name, e.aliases, e.metadata, e.locked,
       e.modified || ':' || LENGTH(e.content_html) AS text_version,
       (SELECT group_concat(label, ', ') FROM (SELECT label FROM entry_labels WHERE entry_id = e.id ORDER BY label)) AS labels
     FROM lore_entries e ORDER BY e.title COLLATE NOCASE`,
  );
  // A few entries' text (those edited since the last search) is read one at a time; past
  // that (the first search of a realm, say), it's quicker to read all of it at once.
  let loaded = 0;
  let all: Map<string, string> | null = null;
  const contentOf = (id: string): string => {
    if (all) return all.get(id) ?? '';
    if (++loaded > ONE_BY_ONE) {
      all = new Map(conn.all('SELECT id, content_html FROM lore_entries WHERE locked = 0').map((r) => [text(r['id']), text(r['content_html'])]));
      return all.get(id) ?? '';
    }
    return text(conn.all('SELECT content_html FROM lore_entries WHERE id = ?', [id])[0]?.['content_html']);
  };

  const entries: EntryHits[] = [];
  let matches = 0;
  let entriesMatched = 0;
  let lockedSkipped = 0;
  for (const row of rows) {
    const id = text(row['id']);
    const locked = int(row['locked']) !== 0;
    if (locked) lockedSkipped++;
    const hits: SearchHit[] = [];
    let count = 0;
    let replaceable = 0;
    const look = (field: SearchField, where: string, value: string) => {
      for (const m of findInText(value, pattern)) {
        count++;
        if (REPLACEABLE.has(field)) replaceable++;
        if (hits.length < hitsPerEntry) {
          hits.push({ field, where, before: snippetBefore(value, m.start), text: m.text, after: snippetAfter(value, m.end) });
        }
      }
    };

    look('title', 'Title', text(row['title']));
    // As the desktop's Find reads them: aliases joined with ", ".
    look('aliases', 'Also known as', text(row['aliases']).split(/\r\n|\r|\n/).map((a) => a.trim()).filter((a) => a !== '').join(', '));
    look('labels', 'Labels', text(row['labels']));
    for (const [name, value] of parseJavaProperties(text(row['metadata']))) {
      look('field-name', 'Field name', name);
      look('field', `Field: ${name}`, value);
    }
    if (!locked) for (const block of blocksOf(id, text(row['text_version']), () => contentOf(id))) look('text', 'Text', block);

    if (count === 0) continue;
    matches += count;
    entriesMatched++;
    if (entries.length < maxEntries) {
      const entityType = text(row['entity_type']);
      const custom = text(row['custom_type_name']);
      entries.push({
        entryId: id,
        title: text(row['title']),
        typeName: entityType === 'CUSTOM' && custom !== '' ? custom : (ENTITY_TYPE_NAMES[entityType] ?? entityType),
        count,
        replaceable,
        version: text(row['text_version']),
        hits,
      });
    }
  }
  return { entries, matches, entriesMatched, lockedSkipped };
}
